// Customer-facing wording. Internal names stay in code; the UI uses these labels.
import { MAX_LISTED_COMPETITORS } from "@/core/research";
import type { FeatureRemovalEffects } from "@/core/catalog-features";
import type { AgentToolName, Quadrant, Rating, RatingSource, RuleCheckStatus, Severity, SourceKind, StepCode } from "@/domain/types";

/** A Claude model id in a few words: "claude-sonnet-5-5" reads "Sonnet 5.5". Any other name is shown as it is. */
export function modelShortName(id: string | null | undefined): string {
  const m = /^claude-([a-z]+)-(\d+)(?:-(\d+))?$/.exec(id ?? "");
  if (!m) return id ?? "";
  return `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}${m[3] ? `.${m[3]}` : ""}`;
}
export const SOURCE_LABEL: Record<SourceKind, string> = { Computed: "Calculated", Recommended: "Suggested", Decided: "Your decision" };
/** Every finding reads as a Warning in Assessment and the audit downloads, including a blocking one. */
export const SEVERITY_LABEL: Record<Severity, "Warning"> = { block: "Warning", warn: "Warning" };
/** A finding in the rules check of a draft: a kernel rule error stops publishing, so it says so. */
export const ISSUE_LABEL: Record<Severity, string> = { block: "Blocks publishing", warn: "Warning" };
/** How each kernel rule came out on the draft. */
export const RULE_CHECK_LABEL: Record<RuleCheckStatus, string> = { met: "Met", warn: "Warning", block: "Blocks publishing" };
/** How many kernel rules came out one way: "2 block publishing", "1 warning", "3 met". */
export function ruleTally(status: RuleCheckStatus, n: number): string {
  if (status === "block") return `${n} ${n === 1 ? "blocks" : "block"} publishing`;
  if (status === "warn") return `${n} ${n === 1 ? "warning" : "warnings"}`;
  return `${n} met`;
}
export const QUADRANT_HINT: Record<Quadrant, string> = {
  Leader: "High value, used by most customers. Anchors an edition.",
  Differentiator: "High value, used by fewer customers. Sets editions apart.",
  Killer: "Low value, rarely used. Can drag an edition down.",
  Filler: "Low value, widely used. Nice to have, not a reason to buy.",
};
/**
 * The four rating words as people read them. The stored words stay Low, Medium, High, Very High (the database,
 * the backend and the assistant use them); only the names on screen change. Same order, same scores, same squares.
 */
export const RATING_LABEL: Record<Rating, string> = { Low: "Very low", Medium: "Low", High: "High", "Very High": "Very high" };
/** A stored rating word as shown, or a fallback when it is not set. */
export const ratingLabel = (r: Rating | null | undefined, unset = "Not rated") => (r ? RATING_LABEL[r] ?? r : unset);
/**
 * What an import file may say, to the stored word. Lower case, one space between words or none (verylow, veryhigh). The screen's words win where the scales share a word
 * ("low" is the screen's Low, stored Medium). "medium" is the old stored word, still read so existing files import.
 * The same table is RATING_OF_IMPORT_WORD in gw-oxley-be/app/constants/agent.py.
 */
export const RATING_OF_IMPORT_WORD = new Map<string, Rating>([
  ["very low", "Low"], ["verylow", "Low"], ["low", "Medium"], ["medium", "Medium"], ["high", "High"], ["very high", "Very High"], ["veryhigh", "Very High"],
]);
/** The import's message for a rating it cannot read. The same as RATING_IMPORT_ERROR in gw-oxley-be/app/constants/agent.py (a test there reads this file). */
export const RATING_IMPORT_ERROR = "Rating must be Very low, Low, High or Very high, or a number from 1 to 4";
/** The 2x2, left to right and top to bottom. X is buyer value (low to high), Y is adoption (few to many). */
export const QUADRANT_LAYOUT: Array<{ q: Quadrant; title: string; hint: string }> = [
  { q: "Filler", title: "Fillers", hint: "low value · many" },
  { q: "Leader", title: "Leaders", hint: "high value · many" },
  { q: "Killer", title: "Killers", hint: "low value · few" },
  { q: "Differentiator", title: "Differentiators", hint: "high value · few" },
];
/** Who an add-on is sold to: its segments, or for an add-on from before segments existed, its editions. */
export function addonScope(a: { segments?: string[]; edition_ids?: string[] }, packages: Array<{ id: string; name: string }>): string {
  if (a.segments?.length) return `for ${a.segments.join(", ")}`;
  const editions = (a.edition_ids ?? []).map((id) => packages.find((p) => p.id === id)?.name).filter(Boolean);
  return `for ${editions.length ? editions.join(", ") : "all editions"}`;
}
/** Step names follow the client's phase names. */
export const STEP_LABEL: Record<StepCode, string> = {
  Intake: "Intake & validation", Research: "Research", Classify: "Feature classification", Drivers: "Value drivers",
  Construct: "Package design", Validate: "Validation", Publish: "Publish",
};
/** Brand Promise: every package delivers it fully. Flexible: delivery steps up across Good, Better, Best. */
export function driverTypeLabel(brandPromise: boolean | null | undefined): string {
  if (brandPromise == null) return "Type not set";
  return brandPromise ? "Brand Promise" : "Flexible";
}
/** The two driver types a person can choose; choosing the selected one again leaves the type not set. */
export const DRIVER_TYPE_OPTIONS = [true, false] as const;
export const DRIVER_TYPE_HINT = "Brand Promise: every package delivers it fully. Flexible: delivery steps up across Good, Better, Best. You decide; uniqueness does not.";

/** Option sets: applying one saves it into the draft as the person's own change. */
export const optionSetTooLarge = (count: number, limit: number) => `This option set needs ${count} changes, and one save can hold at most ${limit}. Nothing was changed.`;
export const APPLY_OPTION_LABEL = "Apply this option";
export const DRAFT_OPTIONS_HINT = "Two ways to structure the same catalog. Each is saved into the draft when you apply it, and you can change anything after.";
export const OPTION_SET_ALREADY_MATCHES = "This option set already matches your draft.";
export const optionSetSaved = (count: number) => `${count} ${count === 1 ? "change" : "changes"} saved to the draft. A starting point for discussion.`;
export const optionSetNothingToChange = (skipped: string[]) => `Nothing to change: ${skipped.join(" ")}`;
export const optionSetSkipped = (skipped: string[]) => `Skipped: ${skipped.join(" ")}`;

/** Structural checks 4 and 7 are judgment calls. The assistant answers them, and the prompt makes it label the reply as guidance. */
export const STRUCTURAL_GUIDANCE_QUESTION: Record<4 | 7, string> = {
  4: "Review the Good, Better and Best editions: do they tell a coherent story from one edition to the next? Label your answer as guidance for the consultant to review, not as a rule result.",
  7: "Review the Good edition: could it satisfy a Better buyer forever, so there is no reason to step up? Label your answer as guidance for the consultant to review, not as a rule result.",
};

/** Intake: ratings and value drivers are optional, so the line is a count, not a to-do list. The chat does the work of filling them in. */
export const intakeOptionalLine = (rated: number, total: number, drivers: number) =>
  `${rated} of ${total} features rated · ${drivers} value driver${drivers === 1 ? "" : "s"}. These are optional. Ask Oxley Assistant in the chat to propose them.`;

/**
 * Oxley Assistant writes into the draft and marks what it wrote, until a person keeps it, removes it or edits it. Publish is never blocked
 * by marked values: Validate warns, and publishing says how many were never reviewed. A version leaves the marks out.
 */
export const ASSISTANT_MARK_LABEL = "Oxley Assistant";
export const KEEP_LABEL = "Keep";
export const REMOVE_LABEL = "Remove";
export const KEEP_ALL_LABEL = "Keep all";
export const UNDO_RUN_LABEL = "Undo these changes";
/** Keeps every value Oxley Assistant wrote in one place (a step, or one chat reply) in one click, as Keep does for one value. */
export const ACCEPT_ALL_LABEL = "Accept all";
export const MARK_SOURCE_LINK_LABEL = "Source";
const valueCount = (n: number) => `${n} ${n === 1 ? "value" : "values"}`;
export const assistantValuesWarning = (n: number) =>
  n === 1 ? "1 value is still the assistant's suggestion. Keep it or change it, or publish as it is." : `${n} values are still the assistant's suggestions. Keep them or change them, or publish as they are.`;
export const assistantValuesAfterPublish = (n: number) => `${valueCount(n)} from Oxley Assistant ${n === 1 ? "was" : "were"} published without a review.`;
/** On a draft that is no longer open: the values nobody reviewed before it was published. */
export const assistantValuesUnreviewed = (n: number) => `${valueCount(n)} from Oxley Assistant ${n === 1 ? "was" : "were"} not reviewed before publishing.`;
/** What each mark's field is called when a value driver's own field was filled in. */
const MARK_FIELD_LABEL: Record<string, string> = { uniqueness: "uniqueness", brand_promise: "Brand Promise or Flexible", description: "description" };
/** A marked value in words; the controls on it name themselves with this, so each button is told apart by a screen reader. */
export const markText = {
  rating: (feature: string) => `Rating of ${feature}`,
  driver: (driver: string) => `Value driver ${driver}`,
  driverField: (driver: string, field: string) => `${driver}: ${MARK_FIELD_LABEL[field] ?? field}`,
  link: (feature: string, driver: string) => `${feature} supports ${driver}`,
  threshold: (edition: string) => `Value threshold of ${edition}`,
  move: (feature: string) => `Where ${feature} sits`,
  disposition: (feature: string) => `Decision on ${feature}`,
};
export const keepMarkLabel = (what: string) => `${KEEP_LABEL}: ${what}`;
export const removeMarkLabel = (what: string) => `${REMOVE_LABEL}: ${what}`;
/** The badge's tooltip: why the assistant wrote it, and where it found it. */
export const markTooltip = (reason: string, sourceLabel: string) => [reason, sourceLabel ? `Source: ${sourceLabel}` : ""].filter(Boolean).join("\n");
/** Ask Oxley's "add to draft": the person's own edit, so it carries no mark. */
export const ASK_ADDED_TEXT = "Added to the draft.";
export const MARKED_VALUES_LIST_LABEL = "Values still marked as Oxley Assistant's";
/** What Remove and Undo say they did, from the server's count of the values it put back. */
export const valuesPutBack = (n: number) => `${valueCount(n)} put back.`;
export const NO_VALUES_PUT_BACK_TEXT = "No values were put back.";
/** Said under Keep all once the list of unreviewed values is gone. */
export const ALL_VALUES_KEPT_TEXT = "Every value from Oxley Assistant is kept.";
/** "Undo these changes" names the reply it undoes by its first words, so a screen reader tells the buttons of two replies apart. */
export const UNDO_REPLY_WORDS = 5;
export const undoRunAriaLabel = (reply: string) => {
  const start = reply.trim().split(/\s+/).slice(0, UNDO_REPLY_WORDS).join(" ");
  return start ? `${UNDO_RUN_LABEL}: reply starting “${start}”` : UNDO_RUN_LABEL;
};
/** Accept all under a chat reply is named the same way, so each reply's button is told apart. */
export const acceptAllAriaLabel = (reply: string) => {
  const start = reply.trim().split(/\s+/).slice(0, UNDO_REPLY_WORDS).join(" ");
  return start ? `${ACCEPT_ALL_LABEL}: reply starting “${start}”` : ACCEPT_ALL_LABEL;
};
/** What Accept all says it did. */
export const valuesAccepted = (n: number) => `${valueCount(n)} accepted.`;
/** Under an agent reply: what the assistant left alone (a value a person set, a feature not rated), a short list and a count of the rest. */
export const SKIPPED_LIST_LABEL = "Left alone by Oxley Assistant";
export const SKIPPED_SHOWN = 5;
export const skippedMore = (n: number) => `and ${n} more`;
/** The deliverable's stamp: values Oxley Assistant wrote that nobody has kept, changed or removed. */
export const valuesNotReviewed = (n: number) => `${valueCount(n)} not yet reviewed`;
export const CLASSIFY_NOT_CLASSIFIED_HINT = "Oxley Assistant proposes a quadrant from its knowledge of the market and the competitors you ticked, and marks what it writes; you keep, change or remove each.";
/** Where a rating came from (PC2-8.8), said in the chip's tooltip so the name stays easy to read. */
export const RATING_SOURCE_LABEL: Record<RatingSource, string> = {
  public: "Rated from the vendor's public website",
  data: "Rated from customer data",
  judgment: "Rated by a person",
  general_knowledge: "Rated from Oxley Assistant's knowledge of the market",
};
/** The banner while Propose classifications runs, and how many pulsing placeholder chips each square shows after its own. */
export const classifyRunning = (n: number) => `Oxley Assistant is rating ${n} ${n === 1 ? "feature" : "features"}…`;
export const RATING_PLACEHOLDERS_PER_SQUARE = 3;
export const ratingsFromAssistant =(n: number) => `${n} ${n === 1 ? "rating" : "ratings"} from Oxley Assistant in the draft, marked.`;
/** Propose classifications done: a short notice; each rating's reason and basis are in the assistant's note in the chat. */
export const classifyDone = (rated: number, leftOut: number) =>
  `Rated ${rated} ${rated === 1 ? "feature" : "features"}${leftOut ? `, ${leftOut} left out` : ""}. The details are in the chat.`;
export const statementsFromAssistant = (n: number) => `${n} ${n === 1 ? "statement" : "statements"} from Oxley Assistant in the draft, marked.`;
export const driverValuesFromAssistant = (n: number) => `${valueCount(n)} on the value drivers from Oxley Assistant in the draft, marked.`;
export const mappingsFromAssistant = (n: number) => `${n} ${n === 1 ? "mapping" : "mappings"} from Oxley Assistant in the draft, marked.`;
/** Said on Validate beside the list of marked values, and shown on a draft row in the Catalog. */
export const assistantValuesInDraft = (n: number) => `${valueCount(n)} from Oxley Assistant`;
/** The chat agent's tool steps as people read them. The backend sends only the tool's name. */
export const TOOL_PROGRESS_LABEL: Record<AgentToolName, string> = {
  web_search: "Searching the web",
  propose_competitors: "Suggesting competitors",
  kb_index: "Looking through the Blue Rocket method",
  kb_search: "Searching the Blue Rocket method",
  kb_read: "Reading the packaging guide",
  propose_ratings: "Proposing ratings",
  propose_value_drivers: "Proposing value drivers",
  map_features: "Linking features to value drivers",
  propose_value_thresholds: "Drafting value threshold statements",
  stage_package_option: "Setting up the Good, Better, Best option",
  run_rules_check: "Checking the packaging rules",
  propose_dispositions: "Recording Killer decisions",
  propose_categories: "Proposing categories",
  map_features_to_categories: "Assigning features to categories",
  set_active_grouping: "Switching grouping",
};
/** Shown for a tool this app has no label for yet, so a new backend tool never shows as "undefined". */
export const TOOL_PROGRESS_FALLBACK = "Working";
export function toolLabel(name: string): string {
  return Object.hasOwn(TOOL_PROGRESS_LABEL, name) ? TOOL_PROGRESS_LABEL[name as AgentToolName] : TOOL_PROGRESS_FALLBACK;
}
/** The assistant's reply stream could not be read: shown when a line is not JSON. */
export const STREAM_READ_ERROR = "The reply from Oxley Assistant could not be read. Try again.";
/** Added to a step's label while it runs. */
export const TOOL_RUNNING_SUFFIX = "…";
export const TOOL_STEPS_LABEL = "What Oxley Assistant is doing";
export const TOOL_FAILED_NOTE = "could not finish";
/** Read out by screen readers after a finished step; sighted people see a tick. */
export const TOOL_DONE_NOTE = "(done)";
/** The label on a line of a reply that says what the assistant wrote into the draft. */
export const REPLY_WRITTEN_LABEL = "Added to draft";

/** Research step wording. Generate hands the work to the chat, which shows its progress, so the step has no wait text of its own. */
export const RESEARCH_SITE_LABEL = "Product website";
export const RESEARCH_BUTTON_LABEL = "Generate competitors using Oxley Assistant";
export const RESEARCH_HANDOFF_HINT = "Tick the competitors the Value drivers step should compare against. You can skip this step.";
export const RESEARCH_COMPETITOR_HELP = `Websites you add arrive ticked. Oxley Assistant's suggestions arrive unticked until you tick them. Up to ${MAX_LISTED_COMPETITORS}.`;
export const RESEARCH_ADDRESS_ERROR = "Enter a website address such as www.example.com, without a user name or password.";
export const RESEARCH_SITE_PLACEHOLDER = "https://www.example.com";
export const RESEARCH_COMPETITOR_PLACEHOLDER = "https://www.competitor.com";
export const COMPETITOR_ADD_LABEL = "Add a competitor manually";
export const COMPETITOR_ADD_BUTTON = "Add";
export const COMPETITOR_REMOVE_BUTTON = "Remove";
/** Why Generate is off, shown beside it. A website that is not an address is explained by RESEARCH_ADDRESS_ERROR, and a running turn by WORK_BUSY_TEXT. */
export const RESEARCH_GENERATE_NEEDS_SITE = "Enter the product website to generate competitors.";
export const RESEARCH_GENERATE_NO_EDIT = "You do not have permission to change this draft.";
/** Shown in place of the competitor list when a change was saved but the list could not be read again, so the rows on screen are out of date. */
export const RESEARCH_SAVED_RELOAD_FAILED = "Saved, but the list could not be reloaded. Reload the page to see it.";
export const RESEARCH_RELOAD_RETRY = "Try again";
export const COMPETITORS_TITLE = "Competitors";
export const competitorCount = (listed: number, ticked: number) => `${listed} of ${MAX_LISTED_COMPETITORS} listed · ${ticked} ticked`;
export const COMPETITORS_EMPTY = "No competitors yet. Add a website, or ask Oxley Assistant to find some.";
export const COMPETITOR_LIST_FULL = `The list holds ${MAX_LISTED_COMPETITORS}. Remove one to add another.`;
export const COMPETITOR_SUGGESTED_BADGE = "Suggested by Oxley Assistant";
export const competitorTickLabel = (website: string) => `Use ${website} in Value drivers`;
export const competitorRemoveLabel = (website: string) => `Remove ${website}`;
/** What the chat shows for the person's own message until the server's saved copy of a request arrives. */
export const competitorsRequested = (website: string) => `Find competitors of ${website}`;
export const DRIVERS_REQUESTED_TEXT = "Propose value drivers";
/** Value drivers step: the action that proposes drivers against the ticked competitors, and the proposed list it fills. */
export const PROPOSE_DRIVERS_LABEL = "Ask Oxley Assistant to propose value drivers";
export const driversUseTicked = (n: number) => `Compares against the ${n} competitor${n === 1 ? "" : "s"} ticked on Research.`;
export const NO_TICKED_COMPETITORS_HINT = "No competitors are ticked on Research: Oxley Assistant finds the main ones itself.";
export const NO_PRODUCT_SITE_FOR_DRIVERS = "Add the product website on Research first.";
/** The Value drivers step's line about drivers the assistant added. */
export const DRIVERS_MARKED_HINT = "Drivers Oxley Assistant adds carry its mark until you keep, change or remove them.";
/** Shown in place of a list that is being read again. */
export const LOADING_TEXT = "Loading...";
/** Said when a turn is asked for, or a mark is kept, removed or undone, while Oxley Assistant is running a turn on the same draft. */
export const WORK_BUSY_TEXT = "Oxley Assistant is still working on this draft. Try again when it has finished.";
/** Shown in the chat while a turn runs on the draft that this panel did not start itself (the person left the page and came back). */
export const WORK_RUNNING_TEXT = "Oxley Assistant is working…";
/** Shown when a turn gets no answer within the time the server allows for it. */
export const AGENT_TURN_TIMEOUT_TEXT = "Oxley Assistant did not answer in time. Try again.";
/** Shown when the reply stream closes with neither a finished reply nor an error, such as when the connection is cut. */
export const AGENT_REPLY_UNFINISHED_TEXT = "The reply did not finish. Try again.";
/** Shown when a save, or a Keep, Remove or Undo, gets no answer in time. The draft is read again, so check it before trying again. */
export const REVIEW_TIMEOUT_TEXT = "Saving took too long. Check the draft before you try again.";
/** The error when the active session's own row is gone, such as when it was deleted. The page shows its generic "Could not load" with Try again, which reads the open sessions again. */
export const SESSION_NOT_FOUND_TEXT = "This session could not be found.";
export const uniquenessText = (n: number) => `Uniqueness ${n}`;
/** Map to value drivers step: the filter above the table, the Unmapped column, the driver counts, and the button that asks to map the rest. */
/** Full screen, for a working area that is cramped in its place in the step (the Package designer, the feature to value driver map). */
export const FULL_SCREEN_LABEL = "Full screen";
export const EXIT_FULL_SCREEN_LABEL = "Exit full screen";
export const fullScreenOpenLabel = (what: string) => `Open ${what} in full screen`;
export const OPEN_IN_FULL_SCREEN_TEXT = "Open in full screen.";
export const FULL_SCREEN_EDIT_HINT = "Changes are saved as you make them. Press Esc to go back.";
export const FULL_SCREEN_READ_ONLY_HINT = "Read only. Press Esc to go back.";
export const MAP_TITLE = "Map to value drivers";
/** What the Full screen button names, for a screen reader: "Open the feature to value driver map in full screen". */
export const MAP_FULL_SCREEN_NAME = "the feature to value driver map";
export const MAP_FEATURE_HEADING = "Feature";
export const MAP_TICK_HINT = "Tick each value driver a feature supports. Each tick is saved to the draft as you make it. A link Oxley Assistant ticked carries its mark until you keep, change or remove it.";
export const ADD_DRIVERS_FIRST_TEXT = "Add value drivers first.";
export const MAP_FILTER_LABEL = "Show features";
export const mapFilterAll = (n: number) => `All features (${n})`;
export const mapFilterUnmapped = (n: number) => `Unmapped (${n})`;
export const mapFilterDriver = (name: string, n: number) => `${name} (${n})`;
export const UNMAPPED_LABEL = "Unmapped";
export const MAPPED_LABEL = "Mapped";
export const driverFeatureCount = (n: number) => `${n} ${n === 1 ? "feature" : "features"}`;
export const NO_FEATURES_YET = "No features yet";
export const NO_UNMAPPED_TEXT = "Every feature supports a value driver.";
export const NO_FEATURES_FOR_DRIVER_TEXT = "No features support this value driver yet.";
export const MAP_TABLE_LABEL = "Features and the value drivers they support";
export const NO_DRAFT_TO_EDIT_TEXT = "There is no draft to change yet.";
export const mapUnmappedLabel = (n: number) => `Ask Oxley Assistant to map ${n === 1 ? "this feature" : `these ${n} features`}`;
/** Uniqueness runs 1 to 4. The backend holds the same top score (MAX_UNIQUENESS), checked by its test against this file. */
export const UNIQUENESS_SCORES = [1, 2, 3, 4] as const;
export const MAX_UNIQUENESS = UNIQUENESS_SCORES[UNIQUENESS_SCORES.length - 1];
/** The top of the old 1 to 5 scale: a file that still has it is read as the new top score. */
export const UNIQUENESS_LEGACY_TOP = 5;
export const UNIQUENESS_LEVEL: Record<(typeof UNIQUENESS_SCORES)[number], string> = {
  1: "every competitor offers it",
  2: "most competitors offer it",
  3: "a few competitors offer it",
  4: "only you, or nearly",
};
export const UNIQUENESS_HINT = "Rate how unique each one is, from 1 (everyone has it) to 4 (only you).";
export const uniquenessOf = (n: number) => `Uniqueness ${n} of ${MAX_UNIQUENESS}`;
/** A driver the assistant proposed may have no uniqueness key at all, so undefined reads as not rated, like null. */
export const uniquenessPill = (n: number | null | undefined) => (n == null ? "Uniqueness not rated" : uniquenessOf(n));
export const uniquenessImportError = (name: string) => `Value driver ${name}: uniqueness must be 1 to ${MAX_UNIQUENESS}`;
/** Suggested prompts above the composer: how many show before "more" (the panel is narrow, so each takes about a line). */
export const VISIBLE_PROMPTS = 2;
export const morePromptsLabel = (n: number) => `${n} more ${n === 1 ? "prompt" : "prompts"}`;
export const FEWER_PROMPTS_LABEL = "Fewer prompts";

export const LATER_RELEASE = "Later release";
/** Catalog import methods shown locked next to the website import. */
export const LOCKED_IMPORT_METHODS = ["From Oxley", "Upload a file", "Upload a document", "From Salesforce", "Manual entry"] as const;
/** Governance tabs shown locked next to Change history. */
export const LOCKED_GOVERNANCE_TABS = ["Rules library", "Assistant history", "Usage details"] as const;
export const NO_ACCESS_MESSAGE = "You do not have access to an organization yet. Ask Blue Rocket to add you.";
export const NO_WORKSPACE_MESSAGE = "No workspace is set up for this organization yet. Ask Blue Rocket to add one.";
/** The top bar's product label when the catalog has no product yet. */
export const NO_PRODUCT_LABEL = "No product yet";
export const PRODUCTS_NOT_LOADED_LABEL = "Products could not be loaded";
export const IMPORTED_SOURCE = "Imported from Oxley";
export const NO_OPEN_SESSION_MESSAGE = "This structure has no open Guided Packaging session to continue.";
/** The Catalog row's one button. The short form (with an arrow) shows when the row is narrow. */
export const GO_TO_GUIDED_PACKAGING = "Go to Guided Packaging";
export const GUIDED_PACKAGING_SHORT = "Guided Packaging";
/** Shown when a Catalog row has no product to pick (such as the seeded sample), so Guided Packaging is not opened. */
export const PRODUCT_NOT_PICKABLE_MESSAGE = "This product cannot be opened in Guided Packaging.";
/** Why a product whose current version is published has no way into Guided Packaging. */
export const PUBLISHED_LOCKED_REASON = "Published versions are locked. Use Upload catalog to start a new version.";
/** Why a product whose draft has no features has no way into Guided Packaging yet. */
export const ADD_FEATURES_FIRST_REASON = "Add features first, on the Features tab or with Upload catalog.";
/** Adding a product by hand: the Catalog's button, its heading links, and the dialog. */
export const ADD_PRODUCT = "Add product";
export const addProductToLineLabel = (line: string) => `Add product to ${line}`;
export const ADD_PRODUCT_DESCRIPTION = "Starts the product as a v1 draft with empty Good, Better and Best editions. Add its features on the Features tab, or read them from its website with Upload catalog.";
export const PRODUCT_NAME_LABEL = "Product name";
export const PRODUCT_LINE_LABEL = "Product line";
export const PRODUCT_LINE_HINT = "Choose one of this customer's lines, or add a new one.";
export const PRODUCT_LINE_PLACEHOLDER = "Choose or add a product line";
export const PRODUCT_LINES_LIST_LABEL = "Product lines";
export const PRODUCT_LINE_SEARCH_LABEL = "Search product lines";
export const PRODUCT_LINE_SEARCH_PLACEHOLDER = "Search or add a new line";
export const addNewLineLabel = (typed: string) => `Add “${typed}” as a new line`;
export const productNameTakenForImport = (name: string) => `This customer already has a product named ${name}. To read a new version of it, choose 'A new version of a product'.`;
export const ADDING_PRODUCT = "Adding…";
export const productAdded = (name: string) => `${name} added as v1. Add its features next.`;
/** The ⋯ menus on a product row, a line heading and a feature row, and their items. */
/** A product row or a feature row, named by what it is called. */
export const rowActionsLabel = (name: string) => `Actions for ${name}`;
export const lineActionsLabel = (line: string) => `Actions for line ${line}`;
/** The Features table's last column, which holds the ⋯ menus; shown to screen readers only. */
export const ACTIONS_COLUMN = "Actions";
export const EDIT_PRODUCT = "Edit product";
export const RENAME_LINE = "Rename line";
export const EDIT_FEATURE = "Edit feature";
export const REMOVE_FEATURE = "Remove feature";
/** The dialogs that edit a product, rename a line, and edit a feature. */
export const EDIT_PRODUCT_DESCRIPTION = "Changes the name and line in this product's draft. The catalog changes when the draft is published.";
export const RENAME_LINE_DESCRIPTION = "Renames the line for every product of this customer in it, published ones too. Published versions keep the old name.";
export const EDIT_FEATURE_DESCRIPTION = "Changes the feature's name and description in this draft. Published versions keep the old ones.";
export const NEW_LINE_NAME_LABEL = "New name";
export const FEATURE_NAME_LABEL = "Name";
export const FEATURE_DESCRIPTION_LABEL = "Description (optional)";
export const SAVE = "Save";
export const SAVING = "Saving…";
export const REMOVING = "Removing…";
export const productUpdated = (name: string) => `${name} saved to its draft.`;
export const lineRenamed = (old: string, next: string) => `${old} renamed to ${next}.`;
export const featureUpdated = (name: string) => `${name} saved.`;
export const featureRemoved = (name: string) => `${name} removed.`;
export const removeFeatureTitle = (name: string) => `Remove ${name}?`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** "a", "a and b", "a, b, and c". */
const listed = (items: string[]) => (items.length < 3 ? items.join(" and ") : `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`);
/** The confirm text for removing a feature: what goes with it, and that there is no undo. */
export function removeFeatureDescription(name: string, effects: FeatureRemovalEffects): string {
  const goes = [
    ...(effects.rated ? ["its rating"] : []),
    ...(effects.driverLinks ? [`its link to ${plural(effects.driverLinks, "value driver", "value drivers")}`] : []),
    ...(effects.editions.length ? [`its place in ${listed(effects.editions)}`] : []),
    ...(effects.addon ? ["its add-on"] : []),
    ...(effects.decided ? ["its Killer decision"] : []),
    ...(effects.assistantValues ? [`${plural(effects.assistantValues, "value", "values")} the assistant wrote`] : []),
  ];
  const taken = `This takes ${name} out of this draft`;
  const goesWith = goes.length ? `${taken}, with ${listed(goes)}.` : `${taken}. Nothing else goes with it.`;
  return `${goesWith} There is no undo. Published versions keep it.`;
}
/** The Structures table with no product in it. */
export const NO_STRUCTURES_YET = "No structures yet. Add a product or upload a catalog to start one.";
/** The Structures table groups products under their product line. */
export const NO_PRODUCT_LINE = "No product line";
export const productLineHeading = (name: string, count: number) => `${name} · ${count} ${count === 1 ? "product" : "products"}`;
/** A draft of a product added by hand, in place of "website import". */
export const HAND_ADDED_SOURCE = "Added by hand";
export const HAND_ADDED_DRAFT_SUBTITLE = "Open draft added by hand, not yet published";
/** Guided Packaging with a product but no draft: "{product} <this> [Catalog] <end>". The Catalog link sits between the two. */
export const NO_DRAFT_IN_AUTHORING = "has no draft in authoring. Published versions are locked. Use Upload catalog on the";
export const UPLOAD_ON_CATALOG_END = "page to start a new version.";
/** Guided Packaging with no product: "No product yet. <this> [Catalog] <end>". */
export const NO_PRODUCT_UPLOAD_START = "Use Upload catalog on the";
export const NO_PRODUCT_UPLOAD_END = "page to start one.";
/** The Ask suggestion card when the draft it would change could not be read (so it cannot say whether there is one). */
export const SUGGESTION_DRAFT_NOT_LOADED = "Suggested change. The draft could not be loaded, so the change cannot be added yet.";
/** Home's link when nothing is in progress: a draft starts from an upload. */
export const UPLOAD_A_CATALOG = "Upload a catalog";
export const PACKAGING_ACCESS_MESSAGE = "You can view this draft. A Packaging author can ask for changes and save them. Ask your Admin for that role.";
export const ROLE_LABEL: Record<string, string> = {
  tenant_admin: "Admin",
  pricing_strategist: "Packaging author",
  finance_approver: "Packaging approver",
  viewer: "Reader",
};
export const ACTION_LABEL: Record<string, string> = {
  "admin.change": "Admin change",
  "support.read": "BlueRocket support",
  "website.import": "Website catalog saved to draft",
  "product.added": "Product added",
  "product_line.renamed": "Product line renamed",
  seed: "Organization set up",
  "draft.opened": "Draft opened",
  "session.started": "Workflow started",
  "session.step": "Moved to step",
  // The staging layer's events: only history written before 23 Oct has them, and the audit page still reads them.
  "change.staged": "Change suggested",
  "change.discarded": "Suggested change removed",
  "draft.commit": "Saved to draft",
  "draft.change": "Saved to draft",
  "assistant.kept": "Assistant value kept",
  "assistant.removed": "Assistant value removed",
  "assistant.undo": "Assistant changes undone",
  "decision.recorded": "Decision note written",
  "kernel.validate": "Checked against rules",
  "draft.submitted": "Submitted for approval",
  "approval.approved": "Approved",
  "approval.rejected": "Rejected",
  "version.published": "Version published",
  "version.superseded": "Version replaced",
  "session.closed": "Workflow finished",
  "draft.closed": "Draft closed",
  "comment.added": "Comment added",
  "thread.resolved": "Comment resolved",
  "oxley.import": "Imported from Oxley",
  "member.joined": "Joined the organization",
  "demo.setup": "Demo set up",
  "demo.role_switch": "Role switched",
  "demo.reset": "Demo reset",
};
export const ACTOR_LABEL: Record<string, string> = { user: "Person", agent: "Assistant", kernel: "Packaging rules", support: "BlueRocket support", system: "System" };

/**
 * The Models tab of Oxley management: the two Claude model choices. Website import reads pricing pages; Guided Packaging
 * covers every step of Guided Packaging, and every other model call (Oxley Assistant included) uses it too.
 */
export const MODEL_SETTING = {
  website: { label: "Website import", hint: "Reads a pricing page, its plans and its icons, when a catalog is uploaded from a website." },
  packaging: { label: "Guided Packaging", hint: "Every Guided Packaging step: the chat, research and the suggestions each step makes. Oxley Assistant uses it too." },
} as const;
export const MODELS_INTRO = "Choose the Claude model for website import and the one for Guided Packaging. Claude Sonnet 5.5 is the default for both.";
export const MODELS_APPLY_NOTE = "Changes apply to the next request. A request already running finishes on the model it started with.";
export const MODELS_SAVED = "AI models saved. The next request uses them.";

/** The Intake step's "What's next": what each later Guided Packaging step does, in one line. */
export const STEP_PURPOSE: Record<Exclude<StepCode, "Intake">, string> = {
  Research: "List the product's competitors, by hand or with Oxley Assistant, and tick the ones Value drivers should compare against.",
  Classify: "Rate each feature on the 2x2 by how much buyers value it and how many use it, and decide what to do with Killers.",
  Drivers: "Oxley Assistant proposes value drivers against the ticked competitors; rate how unique each one is and tick the features that support it.",
  Construct: "Arrange the features into editions and write each edition's value threshold.",
  Validate: "Check the editions against the packaging rules before you publish.",
  Publish: "Review the package design and publish it as the product's next version. A published draft is locked.",
};
/** One line on the Intake step about the framework the editions follow. */
export const FRAMEWORK_NOTE = "Each edition includes everything in the edition below it and adds more on top.";

/** A draft that is published, or was submitted for approval before approvals were removed, cannot change. */
export const DRAFT_LOCKED_TEXT = "This draft is ready to publish and locked: it cannot be edited. Publish it from the Publish step.";
/** The warning before publishing: what it does, and that it cannot be undone. */
export const PUBLISH_WARNING = (product: string, label: string) =>
  `This publishes ${product} ${label} to the catalog. The draft is locked once it is published and cannot be edited again. To change ${product} later, upload the catalog again to start a new version.`;
/** Above a product's versions in the Catalog: they can be looked at, not changed. */
export const VERSIONS_VIEW_ONLY = "Versions are view only. A published version is locked; a draft is changed in Guided Packaging. To start a new version, use Upload catalog.";
/** A version shown step by step in the Catalog's View, and the notice an attempt to change it gets. */
export const VIEW_ONLY_NOTICE_TITLE = "This version is view only";
export const VIEW_ONLY_NOTICE_TEXT = "You can look through every step, but nothing here can be changed. To change this product, use Upload catalog to start a new version.";

/** Oxley management: the Customers tab, the New customer and Edit customer dialogs. */
export const CUSTOMERS_TAB = "Customers";
export const ACCESS_PAGE_SUBTITLE = "People and their roles, customers, and the AI models Oxley uses.";
export const CUSTOMERS_EMPTY = "No customers yet. Add one from the customer menu at the top right.";
export const CUSTOMER_COLUMN = "Customer";
export const WEBSITE_COLUMN = "Website";
export const EDIT_CUSTOMER = "Edit customer";
export const customerSaved = (name: string) => `${name} saved.`;
export const CUSTOMER_NAME_REQUIRED = "Enter a customer name.";
export const customerNameTooLong = (max: number) => `Use ${max} characters or fewer.`;
export const CUSTOMER_NAME_TAKEN = "Another customer already has this name.";
export const CUSTOMER_WEBSITE_ERROR = "Enter a website address that starts with http:// or https://, or one such as example.com.";
export const REPLACE_LOGO = "Replace";
export const REMOVE_LOGO = "Remove";
/** The accessible names of the two buttons above, which say what they act on. */
export const REPLACE_LOGO_LABEL = "Replace logo";
export const REMOVE_LOGO_LABEL = "Remove logo";
/** The customer is saved or created, but reading the customer list again failed: the screen may still show the old list. */
export const customerListNotRefreshed = (message: string) => `Saved, but the customer list could not be refreshed: ${message}`;
export const customerCreatedNotRefreshed = (name: string, message: string) => `${name} was created, but the customer list could not be refreshed: ${message}`;
/** The fields both customer dialogs share. */
export const CUSTOMER_NAME_LABEL = "Name";
export const CUSTOMER_NAME_PLACEHOLDER = "Customer name";
export const CUSTOMER_LOGO_LABEL = "Logo";
export const CUSTOMER_LOGO_UPLOAD_LABEL = "Upload logo";
export const CUSTOMER_LOGO_PREVIEW_ALT = "Logo preview";
export const CUSTOMER_LOGO_CURRENT_ALT = "Current logo";
export const CUSTOMER_LOGO_CLEAR_FILE_LABEL = "Remove logo";
export const CUSTOMER_LOGO_DROP_TITLE = "Choose a file or drag & drop it here";
export const CUSTOMER_LOGO_DROP_HINT = "PNG, JPEG, SVG, WebP · up to 2 MB";
export const BROWSE_FILE = "Browse File";
export const CUSTOMER_WEBSITE_LABEL = "Website URL (optional)";
export const CUSTOMER_WEBSITE_PLACEHOLDER = "https://example.com";
